import React, { useEffect, useState, useRef } from "react";
import { useAppDispatch as useDispatch } from "src/core/redux/hooks";
import { setPageTitle, removeInfoMessage } from "src/core/redux";
import { SentinelBasePage } from "src/web/pages/sentinel-base";
import {
    Alert,
    Box,
    Button,
    Form,
    FormField,
    Header,
    Icon,
    Input,
    Link,
    Pagination,
    Popover,
    SpaceBetween,
    StatusIndicator,
    Table,
    TextFilter,
} from "@cloudscape-design/components";
import { submitPackageDryRunUpgrades, getPackageDryRunUpgradesResult } from "src/core/sentinel/actions";
import { SentinelController } from "src/core/controller";
import { DependencyUpgradeRecommendation, DryRunResult } from "src/core/sentinel/types";
import { useSearchParams } from "react-router-dom";

interface DryRunPageProps {
  sentinelController: SentinelController;
}

export const DryRunPage: React.FC<DryRunPageProps> = ({ sentinelController }) => {
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const [packageName, setPackageName] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [polling, setPolling] = useState(false);
    const [dryRunResult, setDryRunResult] = useState<DryRunResult | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [filteringText, setFilteringText] = useState("");
    const [currentPageIndex, setCurrentPageIndex] = useState(1);
    const pollingRef = useRef<NodeJS.Timeout | null>(null);
    const pageSize = 50;
    const busy = submitting || polling;

    useEffect(() => {
        dispatch(setPageTitle("Dry Run Mode"));
        dispatch(removeInfoMessage({ id: "message_1" }));
        return () => {
            if (pollingRef.current) clearInterval(pollingRef.current);
        };
    }, []);

    useEffect(() => {
        const urlRequestId = searchParams.get("requestId");
        if (urlRequestId) {
            setPolling(true);
            pollForResult(urlRequestId);
        }
    }, []);

    const pollForResult = (requestId: string) => {
        pollingRef.current = setInterval(async () => {
            try {
                const response: any = await dispatch(
                    getPackageDryRunUpgradesResult({ sentinelController, requestId })
                ).unwrap();
                const result = response?.payload as DryRunResult;
                if (result && result.status !== "PROCESSING") {
                    if (pollingRef.current) clearInterval(pollingRef.current);
                    setDryRunResult(result);
                    setPolling(false);
                    if (result.status === "FAILED") {
                        setError(result.errorMessage || "Dry run analysis failed");
                    }
                }
            } catch (e) {
                if (pollingRef.current) clearInterval(pollingRef.current);
                setError(`Failed to poll dry run result for requestId: ${requestId}`);
                setPolling(false);
            }
        }, 5000);
    };

    const handleSubmit = async () => {
        if (!packageName.trim()) return;
        setError(null);
        setDryRunResult(null);
        setSubmitting(true);
        setPolling(false);
        setFilteringText("");
        setCurrentPageIndex(1);
        try {
            const response: any = await dispatch(
                submitPackageDryRunUpgrades({ sentinelController, packageName: packageName.trim() })
            ).unwrap();
            setSubmitting(false);
            const result = response?.payload as DryRunResult;
            if (!result) {
                setError(`No response received for package: ${packageName}`);
                return;
            }
            setDryRunResult(result);
            setSearchParams({ requestId: result.requestId });
            if (result.status === "COMPLETED") {
                // Already done, no polling needed
            } else if (result.status === "PROCESSING") {
                setPolling(true);
                pollForResult(result.requestId);
            } else {
                setError(result.errorMessage || `Dry run failed for package: ${packageName}`);
            }
        } catch (e) {
            setSubmitting(false);
            setError(`Failed to submit dry run for package: ${packageName}`);
        }
    };

    const recommendations = dryRunResult?.dependencyUpgradeRecommendations || [];
    const filteredItems = recommendations.filter((r) =>
        !filteringText ||
    [r.dependencyName,
        r.currentVersion,
        r.currentVersionStatus,
        r.upgradableVersion === "NA" ? "Not Available" : r.upgradableVersion,
        r.upgradeRecommendation.includes("LOW_RISK") ? "AT_RISK" : r.upgradeRecommendation]
        .join(" ")
        .toLowerCase()
        .includes(filteringText.toLowerCase())
    );
    const paginatedItems = filteredItems.slice(
        (currentPageIndex - 1) * pageSize,
        currentPageIndex * pageSize
    );
    const totalPages = Math.ceil(filteredItems.length / pageSize);

    return (
        <SentinelBasePage header="Generate Upgrade Recommendation List">
            <SpaceBetween size="l">
                <Form
                    actions={
                        <SpaceBetween direction="vertical" size="s">
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button
                                    variant="primary"
                                    disabled={busy || !packageName.trim()}
                                    loading={submitting}
                                    onClick={() => handleSubmit()}
                                >
                  Submit Dry Run
                                </Button>
                            </SpaceBetween>
                        </SpaceBetween>
                    }
                >
                    <FormField>
                        <div className="formfield-margin">
                            <strong>Code Package Name</strong>
                            <Input
                                type="text"
                                placeholder="Please enter package name"
                                value={packageName}
                                onChange={(event) => setPackageName(event.detail.value)}
                                disabled={busy}
                            />
                        </div>
                    </FormField>
                </Form>

                {error && (
                    <Alert type="error" dismissible onDismiss={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {polling && (!dryRunResult || dryRunResult?.status === "PROCESSING") && (
                    <SpaceBetween size="s">
                        <SpaceBetween size="xs">
                            <Box><strong>Package Name:</strong> {dryRunResult?.packageName || "..."}</Box>
                            <Box><strong>Request ID:</strong> {dryRunResult?.requestId || "..."}</Box>
                            <Box><strong>Dry Run Status:</strong> <StatusIndicator type="loading">PROCESSING</StatusIndicator></Box>
                        </SpaceBetween>
                        <Box textAlign="center">
                            <StatusIndicator type="loading">
                Analyzing dependencies for {dryRunResult?.packageName || "package"}... This may take a few minutes.
                            </StatusIndicator>
                        </Box>
                    </SpaceBetween>
                )}

                {dryRunResult && dryRunResult.status === "COMPLETED" && (
                    <SpaceBetween size="l">
                        <SpaceBetween size="xs">
                            <Box><strong>Package Name:</strong> {dryRunResult?.packageName || "..."}</Box>
                            <Box><strong>Latest Release:</strong> {dryRunResult.latestRelease}</Box>
                            <Box><strong>Dry Run Status:</strong> <StatusIndicator type="success">{dryRunResult.status}</StatusIndicator></Box>
                            <Box><strong>Request ID:</strong> {dryRunResult?.requestId || "..."}</Box>
                            <Box><strong>Total Recommendations:</strong> {dryRunResult.totalRecommendations}</Box>
                        </SpaceBetween>

                        <Table
                            resizableColumns
                            items={paginatedItems}
                            columnDefinitions={[
                                {
                                    id: "dependencyName",
                                    header: "Dependency Name",
                                    cell: (e: DependencyUpgradeRecommendation) => e.dependencyName,
                                    sortingField: "dependencyName",
                                },
                                {
                                    id: "currentVersion",
                                    header: "Current Version",
                                    cell: (e: DependencyUpgradeRecommendation) => e.currentVersion,
                                    sortingField: "currentVersion",
                                },
                                {
                                    id: "currentVersionStatus",
                                    header: "Current Version Status",
                                    cell: (e: DependencyUpgradeRecommendation) =>
                                        e.currentVersionStatus === "END_OF_LIFE"
                                            ? <span style={{ color: "red", fontWeight: "bold" }}>{e.currentVersionStatus}</span>
                                            : e.currentVersionStatus,
                                    sortingField: "currentVersionStatus",
                                },
                                {
                                    id: "upgradableVersion",
                                    header: "Vendor Guidance Recommended Version",
                                    cell: (e: DependencyUpgradeRecommendation) =>
                                        e.upgradableVersion === "NA" ? "Not Available" : e.upgradableVersion,
                                    sortingField: "upgradableVersion",
                                },
                                {
                                    id: "upgradeRecommendation",
                                    header: (
                                        <SpaceBetween direction="horizontal" size="xs">
                      Risk
                                            <Popover
                                                header="How Risk is Determined"
                                                content={
                                                    <SpaceBetween size="xs">
                                                        <Box>Risk is assessed by comparing the current dependency version's vendor guidance status against available upgrades:</Box>
                                                        <Box><strong>AT_RISK</strong> — Current version has negative guidance (END_OF_LIFE, DEPRECATED, etc.) but a recommended upgrade exists. Upgrade is straightforward.</Box>
                                                        <Box><strong>HIGH_RISK</strong> — Current version has negative guidance and no vendor-recommended upgrade is available. Look for alternative packages.</Box>
                                                        <Box><strong>UPGRADE_AVAILABLE</strong> — Current version is acceptable but a newer recommended version exists. Optional upgrade.</Box>
                                                    </SpaceBetween>
                                                }
                                                triggerType="custom"
                                            >
                                                <Link variant="info"><Icon name="status-info" /></Link>
                                            </Popover>
                                        </SpaceBetween>
                                    ),
                                    cell: (e: DependencyUpgradeRecommendation) => {
                                        if (e.upgradeRecommendation.includes("HIGH_RISK")) {
                                            const display = e.upgradeRecommendation.replace(
                                                "(Look for Other alternatives for Upgrade)",
                                                "(Look for other upgradable alternatives)"
                                            );
                                            return <StatusIndicator type="error"><b>{display}</b></StatusIndicator>;
                                        } else if (e.upgradeRecommendation.includes("LOW_RISK")) {
                                            return <span style={{ color: "#ff8c00", fontWeight: "bold" }}>⚠ AT_RISK</span>;
                                        }
                                        return <StatusIndicator type="info">{e.upgradeRecommendation}</StatusIndicator>;
                                    },
                                    sortingField: "upgradeRecommendation",
                                },
                            ]}
                            loading={false}
                            loadingText="Loading recommendations"
                            empty={
                                <Box textAlign="center" color="inherit">
                                    <b>No recommendations found</b>
                                </Box>
                            }
                            filter={
                                <TextFilter
                                    filteringPlaceholder="Filter recommendations"
                                    filteringText={filteringText}
                                    onChange={({ detail }) => {
                                        setFilteringText(detail.filteringText);
                                        setCurrentPageIndex(1);
                                    }}
                                    countText={`${filteredItems.length} matches`}
                                />
                            }
                            header={<Header>Dependency Upgrade Recommendations</Header>}
                            pagination={
                                <Pagination
                                    currentPageIndex={currentPageIndex}
                                    pagesCount={totalPages}
                                    onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
                                />
                            }
                        />
                    </SpaceBetween>
                )}
            </SpaceBetween>
        </SentinelBasePage>
    );
};
